import { autorun, observable } from "mobx";
import {
  IAPICreateUpdatePriceModel,
  IAPIDonationBeneficiaryModel,
  IAPIDonationIncludedOptionsModel,
  IAPIDonationModelForPayment,
  IAPIDonationOptionsModel,
  IAPIDonationPriceModel,
  IAPIDonationRangesMappingModel,
  IAPIDonationRestrictionModel,
  IAPIItemRelatedItemsRequest,
  IAPIOptionsSelectedDataRequest,
  IAPISelectedItemPriceRequest,
  IAPISelectedItemsRequest,
} from "../interfaces";
import { convertFromRaw, convertToRaw, EditorState } from "draft-js";
import {
  DonationPricingOption,
  DonationType,
  ItemBillingType,
  ItemOptionTypes,
  ItemTypes,
  PricingModel,
} from "../enums";
import { MediaModel } from "./MediaModel";
import { InventorySettings, ProductAttributeInputModel } from "./ProductModel";
import {
  CoreInputModelUtil,
  CoreModel,
  getNewErrors,
  InputErrorModel,
  nameof,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  CustomPricingModel,
  PackagePricingModel,
  PriceTermSettings,
  RecurrencePriceSettings,
  StandardPricingModel,
  TierPricingModel,
} from "./Price.model";
import { CategoryModel } from "./CategoryModels";
import { ItemDetailInputModel } from "./ItemDetailInputModel";

export class RelatedItemsCategoriesModel {
  id: string; //ID OF PRODUCT MEMBERHSHIP OR SERIVCE
  name: string;
  isNew: boolean;
  medias: MediaModel[];
  prices: RelatedItemsPriceCategoriesModel[];
  type: string;
  priceDisplay: string;
  selectedPriceIds: string[];
  hasVariants: boolean;
  variants: ProductAttributeInputModel;
  isSellOnOutOfStock: boolean;
  isTrackInventory: boolean;
  onHandInventory: number;
  inStockOnly: boolean;
  selectedVariantIds: string[];
  canChooseAvailableVariants: boolean;
  quantity: string;

  //below properties are for check out items page
  tax: boolean;
  surcharge: boolean;
  relatedItems: boolean;
  return: boolean;
  returnDays: number;
  discount: boolean;
  description: string;
  descriptionEditorRawContent: string;
  descriptionEditorState: EditorState;
  requiredVariantIds: string[];
  recommendedVariantIds: string[];
  donationPriceSettings?: DonationPricingPageInputsModel;
  donationOptions?: DonationOptionsPageModel;
  requiredQuantity: number;
  recommendedQuantity: number;
  includedQuantity: number;
  inventorySettings?: InventorySettings;
  isActive: boolean;
  category?: CategoryModel;
  //this is only used via check out edit api
  donationDetailPageData?: ItemDetailInputModel;
  constructor(data?: Partial<RelatedItemsCategoriesModel>) {
    this.id = data?.id || "";
    this.name = data?.name || "";
    this.isNew = data?.isNew === true;
    this.medias = data?.medias?.map((m) => new MediaModel(m)) || [];
    this.prices =
      (data &&
        data.prices?.map((p) => {
          return new RelatedItemsPriceCategoriesModel(p);
        })) ||
      [];
    this.type = (data && data.type) || "";
    this.priceDisplay = (data && data.priceDisplay) || "";
    this.selectedPriceIds = (data && data.selectedPriceIds) || [];
    this.hasVariants = (data && data.hasVariants) == true;
    this.variants = (data && data.variants) || new ProductAttributeInputModel();
    this.isSellOnOutOfStock = (data && data.isSellOnOutOfStock) == true;
    this.isTrackInventory = (data && data.isTrackInventory) == true;
    this.onHandInventory = (data && data.onHandInventory) || 0;

    this.inStockOnly = this.onHandInventory > 0;
    this.selectedVariantIds = data?.selectedVariantIds || [];
    this.canChooseAvailableVariants = data?.canChooseAvailableVariants == true;
    this.quantity = data?.quantity || "";

    //below properties are for check out items page
    this.tax = data?.tax == true;
    this.surcharge = data?.surcharge == true;
    this.relatedItems = data?.relatedItems == true;
    this.return = data?.return === true;
    this.returnDays = data?.returnDays || 0;
    this.discount = data?.discount == true;
    this.description = data?.description || "";
    this.descriptionEditorRawContent = data?.descriptionEditorRawContent || "";
    this.descriptionEditorState = UtilityMethods.parseEditorState(
      data?.descriptionEditorRawContent || "",
    );
    this.requiredVariantIds = data?.requiredVariantIds || [];
    this.recommendedVariantIds = data?.recommendedVariantIds || [];
    this.donationPriceSettings =
      data?.donationPriceSettings && data?.donationPriceSettings;
    this.donationOptions = data?.donationOptions && data?.donationOptions;
    this.requiredQuantity = data?.requiredQuantity || 0;
    this.recommendedQuantity = data?.recommendedQuantity || 0;
    this.includedQuantity = data?.includedQuantity || 0;
    this.inventorySettings =
      data?.inventorySettings && new InventorySettings(data.inventorySettings);
    this.isActive = data?.isActive === true;
    this.category = data?.category
      ? new CategoryModel(data.category)
      : undefined;

    this.donationDetailPageData =
      data?.donationDetailPageData &&
      new ItemDetailInputModel(data.donationDetailPageData);
  }

  get isItemUnavailable() {
    if (!this.isActive) return true;
    if (this.type !== ItemTypes.Product) return false;
    if (this.isTrackInventory) {
      if (this.hasVariants) {
        const options = this.variants.options.filter(
          (o) => o.isAvailableBasedOnOnHand,
        );
        return options.length === 0;
      } else {
        return this.inventorySettings && this.inventorySettings?.available <= 0;
      }
    }
    return this.inventorySettings ? !this.inventorySettings.isInStock : false;
  }

  isProductItem() {
    return this.type === ItemTypes.Product;
  }

  getValidVariantOptions() {
    return this.variants.options.filter(
      (x) =>
        x.isRemoved === false &&
        (!this.isTrackInventory || (x.onHand && parseFloat(x.onHand) > 0)),
    );
  }

  getValidRequiredVariantOptions() {
    const validVariantOptions = this.getValidVariantOptions();
    const validRequiredVariants = validVariantOptions.filter(
      (x) =>
        this.requiredVariantIds.length === 0 ||
        this.requiredVariantIds.indexOf(x.id) !== -1,
    );
    return validRequiredVariants;
  }

  getValidRecommendedVariantOptions() {
    const validVariantOptions = this.getValidVariantOptions();
    const validRecommendedVariants = validVariantOptions.filter(
      (x) =>
        this.recommendedVariantIds.length === 0 ||
        this.recommendedVariantIds.indexOf(x.id) !== -1,
    );
    return validRecommendedVariants;
  }

  getItemMedia(variantIndex: number): MediaModel[] {
    if (this.hasVariants) {
      if (variantIndex > -1) {
        const option = this.variants.options[variantIndex];
        if (option && option.attributeMappings?.length > 0) {
          return option.attributeMappings[0].medias;
        }
      }

      return [];
    } else {
      return this.medias;
    }
  }

  getAllMedias(
    variantIds?: string[],
    firstTryItemMedia?: boolean,
  ): MediaModel[] {
    if (this.hasVariants) {
      const filteredOptions =
        variantIds && variantIds?.length > 0
          ? this.variants.options.filter((o) => variantIds.includes(o.id))
          : this.variants.options;
      const option = filteredOptions
        .map((m) => m.attributeMappings)
        .reduce((acc, val) => acc.concat(val), []);
      const medias = option
        .map((m) => m.medias)
        .reduce((acc, val) => acc.concat(val), []);
      const uniqueMedia = medias.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.mediaId === value.mediaId),
      );
      if (firstTryItemMedia) {
        return this.medias?.length > 0 ? this.medias : uniqueMedia;
      } else {
        return uniqueMedia || this.medias;
      }
    } else {
      return this.medias;
    }
  }

  static deserializeAPIList(
    data: IAPISelectedItemsRequest[],
  ): RelatedItemsCategoriesModel[] {
    const list: RelatedItemsCategoriesModel[] = [];
    if (Array.isArray(data)) {
      data.forEach((d) => {
        const mappedData = this.deserializeAPIModel(d);
        list.push(mappedData);
      });
    }
    return list;
  }
  static deserializeAPIModel(
    data: IAPISelectedItemsRequest,
  ): RelatedItemsCategoriesModel {
    const prices = RelatedItemsPriceCategoriesModel.deserializeAPIList(
      data.prices,
    );
    const mappedData: Partial<RelatedItemsCategoriesModel> = {
      id: data.id,
      donationDetailPageData: data.donationDetails
        ? ItemDetailInputModel.deserializeAPIModel(data.donationDetails)
        : undefined,
      isActive: data.isActive,
      name: data.name,
      medias:
        data.medias?.map((c) => {
          return MediaModel.deserializeAPIModel(c);
        }) || [],
      prices: prices,
      selectedPriceIds: data.priceIds,
      type: data.type,
      priceDisplay: RelatedItemsCategoriesModel.displayPrice(prices),
      hasVariants: data.hasVariants,
      variants: data.variants
        ? ProductAttributeInputModel.deserializeAPI(data.variants, true)
        : new ProductAttributeInputModel(),
      isSellOnOutOfStock: data.isSellOnOutOfStock,
      isTrackInventory: data.isTrackInventory,
      onHandInventory: data.onHandInventory,
      quantity: data.quantity > 0 ? data.quantity?.toString() : "1",
      selectedVariantIds: data.variantIds,
      canChooseAvailableVariants: data.canChooseAvailableVariants,
      tax: data.tax,
      surcharge: data.surcharge,
      relatedItems: data.relatedItems,
      return: data.return,
      returnDays: data.returnDays,
      discount: data.discount,
      description: data.description,
      descriptionEditorRawContent: data.descriptionEditorRawContent,
      requiredVariantIds: data.requiredVariantIds || [],
      recommendedVariantIds: data.recommendedVariantIds || [],
      donationOptions:
        data.donationOptions &&
        DonationOptionsPageModel.deserializeAPIModel(data.donationOptions),
      donationPriceSettings:
        data.donationPriceSettings &&
        DonationPricingPageInputsModel.deserializeAPIModel(
          data.donationPriceSettings,
        ),
      includedQuantity: data.includedQuantity,
      recommendedQuantity: data.recommendedQuantity,
      requiredQuantity: data.requiredQuantity,
      inventorySettings:
        data.inventorySettings && new InventorySettings(data.inventorySettings),
      category:
        data.category && CategoryModel.deserializeAPIModel(data.category),
    };
    return new RelatedItemsCategoriesModel(mappedData);
  }

  static displayPrice(prices: RelatedItemsPriceCategoriesModel[]): string {
    const defaultPrice = prices?.find((x) => x.isDefaultPrice);
    if (defaultPrice) {
      return defaultPrice.displayPrice;
    }
    return prices?.[0]?.displayPrice;
  }

  createAPIModel(): IAPISelectedItemsRequest {
    return {
      id: this.id,
      isActive: this.isActive,
      isNew: this.isNew,
      name: this.name,
      medias: this.medias.map((m) => m.createAPIModel()),
      prices: this.prices.map((p) => p.createAPIModel()),
      type: this.type as keyof typeof ItemTypes,
      priceIds: this.selectedPriceIds,
      hasVariants: this.hasVariants,
      variants: this.variants.createAPIModel(),
      isSellOnOutOfStock: this.isSellOnOutOfStock,
      isTrackInventory: this.isTrackInventory,
      onHandInventory: this.onHandInventory,
      quantity: parseFloat(this.quantity),
      variantIds: this.selectedVariantIds,
      canChooseAvailableVariants: this.canChooseAvailableVariants,
      tax: this.tax,
      surcharge: this.surcharge,
      relatedItems: this.relatedItems,
      return: this.return,
      returnDays: this.returnDays,
      discount: this.discount,
      description: this.description,
      descriptionEditorRawContent: this.descriptionEditorRawContent,
      requiredVariantIds: this.requiredVariantIds,
      recommendedVariantIds: this.recommendedVariantIds,
      donationOptions: this.donationOptions?.createAPIModel(),
      donationPriceSettings: this.donationPriceSettings?.createAPIModel(),
      requiredQuantity: this.requiredQuantity,
      recommendedQuantity: this.recommendedQuantity,
      includedQuantity: this.includedQuantity,
    };
  }
}
export class RelatedItemsPriceCategoriesModel {
  id: string;
  name: string;
  description: string;
  terms: PriceTermSettings;
  recurrence: RecurrencePriceSettings;
  billingType: keyof typeof ItemBillingType;
  isDefaultPrice: boolean;
  displayPrice: string;
  isActive: boolean;
  priceId: string;
  constructor(data?: Partial<RelatedItemsPriceCategoriesModel>) {
    this.id = (data && data.id) || "";
    this.description = (data && data.description) || "";
    this.name = (data && data.name) || "";
    this.terms = (data && data.terms) || new PriceTermSettings();
    this.recurrence =
      (data && data.recurrence) || new RecurrencePriceSettings();
    this.billingType = (data && data.billingType) || ItemBillingType.Term;
    this.isDefaultPrice = (data && data.isDefaultPrice) == true;
    this.displayPrice = (data && data.displayPrice) || "";
    this.isActive = data?.isActive || false;
    this.priceId = data?.priceId || "";
  }

  isCustomPricing() {
    let isCustomPricing = false;
    switch (this.billingType) {
      case ItemBillingType.Term:
        isCustomPricing = this.terms.pricingModel === "Custom";
        break;
      case ItemBillingType.TermPlusRecurring:
        isCustomPricing = this.terms.pricingModel === "Custom";
        break;
      case ItemBillingType.Recurring:
        isCustomPricing = this.recurrence.pricingModel === "Custom";
        break;
      default:
        break;
    }
    return isCustomPricing;
  }

  createAPIModel(): IAPISelectedItemPriceRequest {
    return {
      id: this.id,
      name: this.name,
      terms: this.terms.createAPIModel(),
      recurrence: this.recurrence.createAPIModel(),
      billingType: this.billingType,
      isDefaultPrice: this.isDefaultPrice,
      isActive: this.isActive,
      description: this.description,
    };
  }
  static deserializeAPIList(
    data: IAPISelectedItemPriceRequest[],
  ): RelatedItemsPriceCategoriesModel[] {
    const list: RelatedItemsPriceCategoriesModel[] = [];
    if (Array.isArray(data)) {
      data.forEach((d) => {
        const mappedData = this.deserializeAPIModel(d);
        list.push(mappedData);
      });
    }
    return list;
  }
  static deserializeAPIModel(
    data: IAPISelectedItemPriceRequest,
  ): RelatedItemsPriceCategoriesModel {
    const terms =
      data.terms && PriceTermSettings.deserializeAPIModel(data.terms);
    const recurrence =
      data.recurrence &&
      RecurrencePriceSettings.deserializeAPIModel(data.recurrence);
    const mappedData: Partial<RelatedItemsPriceCategoriesModel> = {
      id: data.id,
      name: data.name,
      terms: terms,
      recurrence: recurrence,
      billingType: data.billingType,
      isDefaultPrice: data.isDefaultPrice,
      displayPrice: this.formatAmount(this.getPriceData(terms, recurrence)),
      isActive: data.isActive,
      priceId: data.id,
      description: data.description,
    };
    return new RelatedItemsPriceCategoriesModel(mappedData);
  }

  static deserializeAPIModelFromPriceCreateUpdate(
    data: IAPICreateUpdatePriceModel,
  ): RelatedItemsPriceCategoriesModel {
    const terms =
      (data?.termSettings &&
        PriceTermSettings.deserializeAPIModel(data.termSettings)) ||
      undefined;
    const recurrence =
      (data?.recurrenceSettings &&
        RecurrencePriceSettings.deserializeAPIModel(data.recurrenceSettings)) ||
      undefined;
    const mappedData: Partial<RelatedItemsPriceCategoriesModel> = {
      id: data?.id,
      name: data?.name,
      terms: terms || undefined,
      recurrence: recurrence || undefined,
      billingType: data?.billingType,
      isDefaultPrice: data?.isDefaultPrice,
      displayPrice: this.formatAmount(this.getPriceData(terms, recurrence)),
      isActive: data?.isActive,
      priceId: data?.id,
      description: data.description,
    };
    return new RelatedItemsPriceCategoriesModel(mappedData);
  }

  static getPriceData(
    terms?: PriceTermSettings,
    recurrence?: RecurrencePriceSettings,
  ): number | null {
    return terms
      ? this.priceValue(
          terms.pricingModel,
          terms.standardPricing,
          terms.packagePricing,
          terms.customPricing,
          terms.tierPricing,
        )
      : this.priceValue(
          recurrence?.pricingModel || PricingModel.Standard,
          recurrence?.standardPricing,
          recurrence?.packagePricing,
          recurrence?.customPricing,
          recurrence?.tierPricing,
        );
  }

  static formatAmount(amount: number | null): string {
    if (!amount) {
      return "$0";
    }
    return `$${UtilityMethods.numberWithCommas(amount, true)}`;
  }

  static priceValue(
    pricingModel: PricingModel,
    standardPricing: StandardPricingModel | null | undefined,
    packagePricing?: PackagePricingModel | null,
    customPricing?: CustomPricingModel | null,
    tierPricing?: TierPricingModel | null,
  ): number | null {
    switch (pricingModel) {
      case PricingModel.Standard:
        if (standardPricing) {
          if (standardPricing?.originalPrice) {
            return standardPricing?.originalPrice;
          }
          return standardPricing?.price;
        }
        break;
      case PricingModel.Package:
        if (packagePricing) {
          if (packagePricing?.showOriginalPrice) {
            return packagePricing?.options?.[0]?.originalPrice;
          }
          return packagePricing?.options?.[0]?.price;
        }
        break;
      case PricingModel.Volume:
      case PricingModel.Tiered:
        if (tierPricing) {
          if (tierPricing?.showOriginalPrice) {
            return tierPricing.originalPrice;
          }
          return tierPricing?.price;
        }
        break;
      case PricingModel.Custom:
        if (customPricing?.minimum && customPricing?.minimum > 0) {
          return customPricing?.minimum;
        }
    }
    return 0;
  }
}

export class RelatedItemsModel extends CoreModel {
  @observable items: RelatedItemsCategoriesModel[];
  @observable accountCredits: string;
  @observable categories: RelatedItemsCategoriesModel[];
  @observable showSelectItems: boolean;
  @observable showSelectItemsPopup: boolean;
  @observable showAccountCredits: boolean;
  @observable showCategories: boolean;
  @observable hasSelectedAnOption: boolean;
  @observable totalRequired: number;
  errors: InputErrorModel<RelatedItemsModel>[];
  constructor(data?: Partial<RelatedItemsModel>) {
    super();
    this.items = data?.items || [];
    this.accountCredits = data?.accountCredits || "";
    this.categories = data?.categories || [];
    this.showSelectItems = data?.showSelectItems === true;
    this.showSelectItemsPopup = data?.showSelectItemsPopup === true;
    this.showAccountCredits = data?.showAccountCredits === true;
    this.showCategories = data?.showCategories === true;
    this.errors = data?.errors || [];
    this.totalRequired = data?.totalRequired || 1;

    this.hasSelectedAnOption =
      this.showAccountCredits || this.showCategories || this.showSelectItems;
    autorun(() => {
      this.validate(true);
    });
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<RelatedItemsModel>[] = [];

    if (this.showAccountCredits) {
      const isValid = this.validateDecimalValue(
        this.accountCredits,
        "accountCredits",
        newErrors,
      );

      if (isValid && parseFloat(this.accountCredits) <= 0) {
        this.addError("accountCredits", newErrors);
      }
    }
    if (this.showCategories) {
      if (!this.categories || this.categories.length === 0) {
        newErrors.push(
          new InputErrorModel({
            field: nameof<RelatedItemsModel>("categories"),
          }),
        );
      }
    }
    //DO NOT ADD ANY NEW ERROR
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
  static deserializeAPIModel(
    data: IAPIOptionsSelectedDataRequest,
  ): RelatedItemsModel {
    const mappedData: Partial<RelatedItemsModel> = {
      accountCredits: data.accountCredits.toString(),
      showSelectItems:
        data.category === ItemOptionTypes.Item && data.items.length > 0,
      showCategories: data.category === ItemOptionTypes.Category,
      showAccountCredits: data.category === ItemOptionTypes.IssueCredits,
      categories:
        data.items?.map((c) => {
          return RelatedItemsCategoriesModel.deserializeAPIModel(c);
        }) || [],
      items:
        data.items?.map((c) => {
          return RelatedItemsCategoriesModel.deserializeAPIModel(c);
        }) || [],
      totalRequired: data.totalRequired,
    };
    return new RelatedItemsModel(mappedData);
  }

  createAPIModel(): IAPIOptionsSelectedDataRequest | null {
    const itemOptions = this.showAccountCredits
      ? ({
          accountCredits: parseFloat(this.accountCredits),
          category: ItemOptionTypes.IssueCredits,
        } as IAPIOptionsSelectedDataRequest)
      : this.showCategories
        ? ({
            category: ItemOptionTypes.Category,
            items: this.categories.map((c) => {
              return {
                type: ItemTypes.Category,
                id: c.id,
                name: c.name,
                isNew: c.isNew,
              } as IAPISelectedItemsRequest;
            }),
          } as IAPIOptionsSelectedDataRequest)
        : this.showSelectItems
          ? ({
              category: ItemOptionTypes.Item,
              items: this.items.map((c) => {
                return {
                  type: c.type,
                  id: c.id,
                  name: c.name,
                  isNew: false,
                  priceIds: c.selectedPriceIds,
                  canChooseAvailableVariants: c.canChooseAvailableVariants,
                  variantIds: c.selectedVariantIds,
                  quantity: !c.quantity ? 0 : parseFloat(c.quantity),
                } as IAPISelectedItemsRequest;
              }),
              totalRequired: this.totalRequired,
            } as IAPIOptionsSelectedDataRequest)
          : null;
    return itemOptions;
  }
}
export class DonationOptionsPageModel {
  includedItems: DonationIncludedSectionModel;
  recommendedItems: RelatedItemsModel;
  constructor(data?: Partial<DonationOptionsPageModel>) {
    this.recommendedItems = new RelatedItemsModel(data?.recommendedItems);
    this.includedItems = new DonationIncludedSectionModel(data?.includedItems);
    autorun(() => {
      this.validate(true);
    });
  }

  validate(isAutoRun = false): boolean {
    const validIncludedItems = this.includedItems.validate(isAutoRun);
    const validRecommendedItems = this.recommendedItems.validate(isAutoRun);
    return validIncludedItems && validRecommendedItems;
  }

  static deserializeAPIModel(
    data: IAPIDonationOptionsModel,
  ): DonationOptionsPageModel {
    const mappedData: Partial<DonationOptionsPageModel> = {
      includedItems: data.included
        ? DonationIncludedSectionModel.deserializeAPIModel(data.included)
        : new DonationIncludedSectionModel(),
      recommendedItems: data.recommended
        ? RelatedItemsModel.deserializeAPIModel(data.recommended)
        : new RelatedItemsModel(),
    };

    return new DonationOptionsPageModel(mappedData);
  }

  createAPIModel(): IAPIDonationOptionsModel {
    return {
      included: this.includedItems.createAPIModel(),
      recommended: this.recommendedItems.createAPIModel(),
    };
  }
}
export class RelatedSectionModel {
  public includedItems: RelatedItemsModel;
  public requiredItems: RelatedItemsModel;
  public recommendedItems: RelatedItemsModel;
  constructor(data?: Partial<RelatedSectionModel>) {
    this.includedItems = new RelatedItemsModel(data?.includedItems);
    this.requiredItems = new RelatedItemsModel(data?.requiredItems);
    this.recommendedItems = new RelatedItemsModel(data?.recommendedItems);
    autorun(() => {
      this.validate(true);
    });
  }

  validate(isAutoRun = false): boolean {
    this.includedItems.validate(isAutoRun);
    this.recommendedItems.validate(isAutoRun);
    this.requiredItems.validate(isAutoRun);
    return (
      this.includedItems.errors.length === 0 &&
      this.requiredItems.errors.length === 0 &&
      this.recommendedItems.errors.length === 0
    );
  }

  createAPIModel(): IAPIItemRelatedItemsRequest | null {
    const options = {
      includedItems: this.includedItems.createAPIModel(),
      requiredItems: this.requiredItems.createAPIModel(),
      recommendedItems: this.recommendedItems.createAPIModel(),
    } as IAPIItemRelatedItemsRequest;

    const result =
      options.includedItems == null &&
      options.requiredItems == null &&
      options.recommendedItems == null
        ? null
        : options;

    return result;
  }

  static deserializeAPIModel(
    data: IAPIItemRelatedItemsRequest,
  ): RelatedSectionModel {
    const mappedData: Partial<RelatedSectionModel> = {
      includedItems: data.includedItems
        ? RelatedItemsModel.deserializeAPIModel(data.includedItems)
        : new RelatedItemsModel(),
      requiredItems: data.requiredItems
        ? RelatedItemsModel.deserializeAPIModel(data.requiredItems)
        : new RelatedItemsModel(),
      recommendedItems: data.recommendedItems
        ? RelatedItemsModel.deserializeAPIModel(data.recommendedItems)
        : new RelatedItemsModel(),
    };
    return new RelatedSectionModel(mappedData);
  }
}

export class DonationIncludedSectionModel extends CoreModel {
  any: boolean;
  totalRanges: number;
  errors: InputErrorModel<DonationIncludedSectionModel>[];
  ranges: DonationRagnesModel[];
  item: RelatedItemsModel;
  rangeErrors: InputErrorModel<DonationRagnesModel>[];
  constructor(data?: Partial<DonationIncludedSectionModel>) {
    super();
    this.any = !data ? true : data?.any || false;
    this.errors = data?.errors || [];
    this.totalRanges = data?.totalRanges || 2;
    this.rangeErrors = data?.rangeErrors || [];
    this.ranges = data?.ranges?.map((r) => new DonationRagnesModel(r)) || [];
    this.item = new RelatedItemsModel(data?.item);

    if (this.ranges.length > 0) {
      this.ranges = this.ranges.map((r, idx) => {
        if (idx === 0) {
          r.start = "1";
        } else {
          const previousMax = parseFloat(this.ranges[idx - 1].max);
          if (previousMax) {
            r.start = (previousMax + 1).toString();
          } else {
            r.start = "";
          }
        }

        return new DonationRagnesModel(r);
      });
    }
  }

  hasError(key: keyof DonationIncludedSectionModel): boolean {
    return this.errors.some((e) => e.field === key);
  }

  hasRangeError(key: keyof DonationRagnesModel, index: number): boolean {
    return this.rangeErrors.some((e) => e.field === key && e.index === index);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<DonationIncludedSectionModel>[] = [];
    const newRangeErrors: InputErrorModel<DonationRagnesModel>[] = [];
    let hasError = false;

    if (!this.any) {
      if (this.ranges.length === 0) {
        newErrors.push(
          new InputErrorModel<DonationIncludedSectionModel>({
            field: "ranges",
            message: "Please add at least one range",
          }),
        );
      }
      this.ranges.forEach((r, idx) => {
        const isLast = idx + 1 === this.ranges.length;

        if (isLast) {
          if (r.max) {
            this.validateDecimalValue(r.max, "max", newRangeErrors, idx);
          }
        } else {
          this.validateDecimalValue(r.max, "max", newRangeErrors, idx);
        }
        this.validateDecimalValue(r.start, "start", newRangeErrors, idx);

        if (!isLast && idx > 0) {
          const previousRange = this.ranges[idx - 1];
          if (parseFloat(r.max) <= parseFloat(previousRange.max)) {
            this.addError(
              "max",
              newRangeErrors,
              "Max value should be higher than prevous",
              idx,
            );
          } else if (parseFloat(previousRange.max) - parseFloat(r.start) > -1) {
            this.addError(
              "max",
              newRangeErrors,
              "Max value should be continuous",
              idx,
            );
          }
        }

        if (!isLast) {
          if (parseFloat(r.max) <= parseFloat(r.start)) {
            this.addError(
              "max",
              newRangeErrors,
              "Max value should be higher than start",
              idx,
            );
          }
        }

        if (r.item.validate(isAutoRun) === false) {
          hasError = true;
        }

        if (!r.item.hasSelectedAnOption) {
          this.addError(
            "item",
            newRangeErrors,
            "Please select at least one item",
            idx,
          );
        }
      });
    } else {
      if (!this.item.hasSelectedAnOption) {
        // this.addError('item', newErrors, 'Please select at least one item');
      } else {
        if (!this.item.validate(isAutoRun)) {
          hasError = true;
        }
      }
    }

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
      this.rangeErrors = getNewErrors(newRangeErrors, this.rangeErrors);
    } else {
      this.errors = newErrors;
      this.rangeErrors = newRangeErrors;
    }

    return newErrors.length === 0 && newRangeErrors.length === 0 && !hasError;
  }

  static deserializeAPIModel(
    data: IAPIDonationIncludedOptionsModel,
  ): DonationIncludedSectionModel {
    const mappedData: Partial<DonationIncludedSectionModel> = {
      any: data.anyAmount,
      totalRanges: data.totalRanges,
      item: data.item
        ? RelatedItemsModel.deserializeAPIModel(data.item)
        : new RelatedItemsModel(),
      ranges:
        data.ranges?.map((r) => DonationRagnesModel.deserializeAPIModel(r)) ||
        [],
    };

    return new DonationIncludedSectionModel(mappedData);
  }

  createAPIModel(): IAPIDonationIncludedOptionsModel {
    return {
      anyAmount: this.any,
      totalRanges: this.totalRanges,
      item: this.any ? this.item.createAPIModel() : null,
      ranges: !this.any ? this.ranges.map((r) => r.createAPIModel()) : [],
    };
  }
}

export class DonationRagnesModel {
  start: string;
  max: string;
  item: RelatedItemsModel;
  constructor(data?: Partial<DonationRagnesModel>) {
    this.max = data?.max || "";
    this.start = data?.start || "";
    this.item = new RelatedItemsModel(data?.item);
  }

  validate(isAutoRun = false): boolean {
    return this.item.validate(isAutoRun);
  }

  static deserializeAPIModel(
    data: IAPIDonationRangesMappingModel,
  ): DonationRagnesModel {
    const mappedData: Partial<DonationRagnesModel> = {
      max: data.maxAmount ? data.maxAmount.toString() : "",
      start: data.startAmount.toString(),
      item: data.item
        ? RelatedItemsModel.deserializeAPIModel(data.item)
        : new RelatedItemsModel(),
    };

    return new DonationRagnesModel(mappedData);
  }

  createAPIModel(): IAPIDonationRangesMappingModel {
    return {
      maxAmount: parseFloat(this.max),
      startAmount: parseFloat(this.start),
      item: this.item.createAPIModel(),
    };
  }
}
export const DEFAULT_MONTHLY_DONATION_MESSAGE =
  "Make a greater impact with a monthly donation! You can cancel at any time.";

/**
 * Caption shown on the customer-facing donation screen once the donor has
 * actually selected "Monthly". At that point the nudge message
 * (DEFAULT_MONTHLY_DONATION_MESSAGE) no longer applies, so we confirm/thank
 * instead.
 *
 * Plain sentences with no hard breaks: the caption wraps to the measure set by
 * `.monthly-thankyou-caption` in Pricing.styles.ts.
 */
export const MONTHLY_DONATION_THANK_YOU_MESSAGE =
  "Thank you for your monthly donation! It will make a huge difference. You can cancel at any time.";

export class DonationPricingPageInputsModel extends CoreModel {
  title: string;
  titleEditorState: EditorState;
  titleEditorRawContent: string;
  subTitle: string;
  subTitleEditorState: EditorState;
  subTitleEditorRawContent: string;
  pricingOption: keyof DonationPricingOption;
  type: keyof DonationType;
  description: string;
  descriptionEditorState: EditorState;
  descriptionEditorRawContent: string;
  message: string;
  messageEditorState: EditorState;
  messageEditorRawContent: string;
  defaultPrice?: string;
  beneficiaries: DonationBeneficiaryModel[];
  canSelectOneBeneficiary: boolean;
  canSelectMultipleBeneficiaries: boolean;
  allowComments: boolean | null;
  offerInMemoryOf: boolean | null;
  inMemoryOfName: string;
  inMemoryOfComment: string;
  annualIncrease: boolean | null;
  annualIncreaseDescription: string;
  coverProcessingCost: boolean | null;
  coverProcessingCostDescription: string;
  allowAnonymousDonation: boolean | null;
  setLimitOnCustomDonation: boolean;
  minimumDonationAmount: string;
  maximumDonationAmount: string;
  selectedBeneficiaries: DonationBeneficiaryModel[];
  errors: InputErrorModel<DonationPricingPageInputsModel>[];
  //below properties for donation purchas
  donationAmout: number;
  oneTime: boolean;
  annualIncreasePercentage: number;
  customAmount: string;
  purchaseErrors: InputErrorModel<DonationPricingPageInputsModel>[];
  customAmounts: string[];
  selectedCustomIndex: number | null;
  constructor(data?: Partial<DonationPricingPageInputsModel>) {
    super();
    this.title = (data && data.title) || "";
    this.titleEditorState =
      (data && data.titleEditorState) || EditorState.createEmpty();
    this.titleEditorRawContent = (data && data.titleEditorRawContent) || "";
    this.subTitle = (data && data.subTitle) || "";
    this.message = (data && data.message) || "";
    this.inMemoryOfComment = (data && data.inMemoryOfComment) || "";
    this.subTitleEditorState =
      (data && data.subTitleEditorState) || EditorState.createEmpty();
    this.subTitleEditorRawContent =
      (data && data.subTitleEditorRawContent) || "";
    this.inMemoryOfName = (data && data.inMemoryOfName) || "";
    this.selectedCustomIndex = (data && data.selectedCustomIndex) ?? null;
    this.pricingOption =
      (data && data.pricingOption) ||
      (Object.keys(DonationPricingOption).find(
        (key) =>
          DonationPricingOption[key as keyof typeof DonationPricingOption] ===
          DonationPricingOption.Option1,
      ) as keyof DonationPricingOption);

    this.type =
      (data && data.type) ||
      (Object.keys(DonationType).find(
        (key) =>
          DonationType[key as keyof typeof DonationType] ===
          DonationType.OneTimeOrMonthly,
      ) as keyof DonationType);
    this.description = (data && data.description) || "";
    this.descriptionEditorState =
      (data && data.descriptionEditorState) || EditorState.createEmpty();
    this.descriptionEditorRawContent =
      (data && data.descriptionEditorRawContent) || "";
    this.messageEditorState =
      (data && data.messageEditorState) || EditorState.createEmpty();
    this.messageEditorRawContent = (data && data.messageEditorRawContent) || "";

    if (!data && !this.description) {
      this.description = DEFAULT_MONTHLY_DONATION_MESSAGE;
      this.descriptionEditorState = UtilityMethods.getEditorStateFromPlainText(
        DEFAULT_MONTHLY_DONATION_MESSAGE,
      );
      this.descriptionEditorRawContent = JSON.stringify(
        convertToRaw(this.descriptionEditorState.getCurrentContent()),
      );
    }
    this.defaultPrice = (data && data.defaultPrice) || "";
    this.beneficiaries =
      (data &&
        data.beneficiaries?.map((b) => {
          return new DonationBeneficiaryModel(b);
        })) ||
      [];
    this.selectedBeneficiaries =
      (data &&
        data.selectedBeneficiaries?.map(
          (b) => new DonationBeneficiaryModel(b),
        )) ||
      [];
    this.canSelectOneBeneficiary =
      (data && data.canSelectOneBeneficiary) || false;
    this.canSelectMultipleBeneficiaries =
      (data && data.canSelectMultipleBeneficiaries) || false;
    this.allowComments =
      data && data.allowComments
        ? true
        : data?.allowComments === null
          ? null
          : false;
    this.offerInMemoryOf =
      data && data.offerInMemoryOf
        ? true
        : data?.offerInMemoryOf === null
          ? null
          : false;
    this.annualIncrease =
      data && data.annualIncrease
        ? true
        : data?.annualIncrease === null
          ? null
          : false;
    this.annualIncreaseDescription =
      (data && data.annualIncreaseDescription) || "";

    if (!data && !this.annualIncreaseDescription) {
      this.annualIncreaseDescription =
        "Yes, I want to help cover annual increases in expenses.";
    }
    this.coverProcessingCost =
      data && data.coverProcessingCost
        ? true
        : data?.coverProcessingCost === null
          ? null
          : false;
    this.allowAnonymousDonation =
      data && data.allowAnonymousDonation
        ? true
        : data?.allowAnonymousDonation === null
          ? null
          : false;
    this.setLimitOnCustomDonation =
      (data && data.setLimitOnCustomDonation) || false;
    this.minimumDonationAmount = (data && data.minimumDonationAmount) || "";
    this.maximumDonationAmount = (data && data.maximumDonationAmount) || "";
    this.coverProcessingCostDescription =
      (data && data.coverProcessingCostDescription) || "";
    if (!data && !this.coverProcessingCostDescription) {
      this.coverProcessingCostDescription =
        "Yes, I want to cover the cost to process my donation and put more of my money to work.";
    }
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<DonationPricingPageInputsModel>(e);
        })) ||
      [];
    this.donationAmout = (data && data.donationAmout) || 0;
    this.oneTime = (data && data.oneTime) ?? true;
    this.annualIncreasePercentage =
      (data && data.annualIncreasePercentage) || 10;
    this.customAmount = (data && data.customAmount) || "";
    this.customAmounts = data?.customAmounts ?? Array(6).fill("");
    this.purchaseErrors =
      (data &&
        data.purchaseErrors?.map((e) => {
          return new InputErrorModel<DonationPricingPageInputsModel>(e);
        })) ||
      [];
    autorun(() => {
      if (this.beneficiaries.length < 2) {
        this.canSelectMultipleBeneficiaries = false;
      }
      this.validate(true);
      this.validateForCustomerFacing(true);
    });
  }

  hasError(field: keyof DonationPricingPageInputsModel): boolean {
    return (
      this.errors.some((e) => e.field === field) ||
      this.purchaseErrors.some((e) => e.field === field)
    );
  }

  getErrorMessage(field: keyof DonationPricingPageInputsModel): string {
    const error =
      this.errors.find((e) => e.field === field) ||
      this.purchaseErrors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<DonationPricingPageInputsModel>[] = [];
    this.validateTextField(this.title, "title", newErrors);
    // // this.validateTextField(this.subTitle, "subTitle", newErrors);
    // this.validateTextField(this.message, "message", newErrors);

    // The monthly-donation message (description) is no longer required: a
    // default message is always shown in the editor, preview, and customer-
    // facing screen, so an empty field should not raise a validation error.

    this.validateTextField(
      this.pricingOption?.toString(),
      "pricingOption",
      newErrors,
    );
    // if (this.defaultPrice) {
    //     this.validateDecimalValue(this.defaultPrice, 'defaultPrice', newErrors);
    // }

    if (this.canSelectMultipleBeneficiaries || this.canSelectOneBeneficiary) {
      if (this.beneficiaries.length === 0) {
        newErrors.push(
          new InputErrorModel<DonationPricingPageInputsModel>({
            field: "beneficiaries",
          }),
        );
      }
    }

    if (this.setLimitOnCustomDonation) {
      this.validateDecimalValue(
        this.minimumDonationAmount,
        "minimumDonationAmount",
        newErrors,
      );
      this.validateDecimalValue(
        this.maximumDonationAmount,
        "maximumDonationAmount",
        newErrors,
      );
    }

    // if (this.coverProcessingCost) {
    //   this.validateTextField(
    //     this.coverProcessingCostDescription,
    //     "coverProcessingCostDescription",
    //     newErrors
    //   );
    // }

    if (this.annualIncrease) {
      this.validateTextField(
        this.annualIncreaseDescription,
        "annualIncreaseDescription",
        newErrors,
      );
    }

    if (
      DonationPricingOption[
        this.pricingOption as keyof typeof DonationPricingOption
      ] === DonationPricingOption.CustomAll
    ) {
      const seen = new Map<string, number[]>();
      const duplicates = new Set<number>();

      this.customAmounts?.forEach((amount, index) => {
        if (!amount && index !== 5) {
          newErrors.push(
            new InputErrorModel<DonationPricingPageInputsModel>({
              field:
                `customAmounts${index}` as keyof DonationPricingPageInputsModel,
              message: `Custom amount ${index + 1} is required.`,
            }),
          );
        } else {
          if (seen.has(amount)) {
            seen
              .get(amount)
              ?.forEach((duplicateIndex) => duplicates.add(duplicateIndex));
            duplicates.add(index);
            seen.get(amount)?.push(index);
          } else {
            seen.set(amount, [index]);
          }
        }
      });
      duplicates.forEach((index) => {
        newErrors.push(
          new InputErrorModel<DonationPricingPageInputsModel>({
            field:
              `customAmounts${index}` as keyof DonationPricingPageInputsModel,
            message: `Duplicate custom amount at field ${index + 1}. Each value must be unique.`,
          }),
        );
      });
      if (duplicates.size > 0) {
        duplicates.forEach((duplicate) => {
          newErrors.push(
            new InputErrorModel<DonationPricingPageInputsModel>({
              field: "customAmounts" as keyof DonationPricingPageInputsModel,
              message: `Duplicate custom amount found. Each value must be unique.`,
            }),
          );
        });
      }
    }

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  validateForCustomerFacing(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<DonationPricingPageInputsModel>[] = [];
    let isValidCustomAmount = true;
    if (this.donationAmout < 1 && this.customAmount === "") {
      CoreInputModelUtil.validateDecimalValue(
        this.donationAmout.toString(),
        "donationAmout",
        newErrors,
        undefined,
        true,
        "Kindly select valid amount to donate",
      );
    } else if (this.customAmount !== "") {
      isValidCustomAmount = CoreInputModelUtil.validateDecimalValue(
        this.customAmount,
        "customAmount",
        newErrors,
        0,
        true,
        "Kindly enter valid amount to donate",
      );
    }
    if (this.offerInMemoryOf) {
      this.validateTextField(this.inMemoryOfName, "inMemoryOfName", newErrors);
    }
    if (this.allowComments && this.offerInMemoryOf) {
      this.validateTextField(
        this.inMemoryOfComment,
        "inMemoryOfComment",
        newErrors,
      );
    }
    if (this.annualIncrease) {
      CoreInputModelUtil.validateDecimalValue(
        this.annualIncreasePercentage.toString(),
        "annualIncreasePercentage",
        newErrors,
        0,
        true,
        "Please select annual increase percentage",
      );
    }
    if (this.canSelectOneBeneficiary && !this.canSelectMultipleBeneficiaries) {
      if (this.selectedBeneficiaries.length === 0) {
        newErrors.push(
          new InputErrorModel<DonationPricingPageInputsModel>({
            field: "selectedBeneficiaries",
            message: "Kindly select a beneficiary",
          }),
        );
      }
      if (this.selectedBeneficiaries.length > 1) {
        newErrors.push(
          new InputErrorModel<DonationPricingPageInputsModel>({
            field: "selectedBeneficiaries",
            message: "Kindly select only one beneficiary",
          }),
        );
      }
    } else if (
      this.canSelectOneBeneficiary ||
      this.canSelectMultipleBeneficiaries
    ) {
      if (this.selectedBeneficiaries.length === 0) {
        newErrors.push(
          new InputErrorModel<DonationPricingPageInputsModel>({
            field: "selectedBeneficiaries",
            message: "Kindly select a beneficiary",
          }),
        );
      }
    }
    if (this.setLimitOnCustomDonation) {
      if (this.pricingOption !== ("CustomAll" as keyof DonationPricingOption)) {
        if (
          this.customAmount !== "" &&
          isValidCustomAmount &&
          this.customAmount === this.donationAmout.toString()
        ) {
          const customAmount = parseFloat(this.customAmount);
          if (
            customAmount < parseFloat(this.minimumDonationAmount) ||
            customAmount > parseFloat(this.maximumDonationAmount)
          ) {
            newErrors.push(
              new InputErrorModel<DonationPricingPageInputsModel>({
                field: "customAmount",
                message: `Kindly enter an amount in between $${this.minimumDonationAmount} - $${this.maximumDonationAmount}`,
              }),
            );
          }
        }
      } else {
        if (
          this.customAmounts[5] &&
          CoreInputModelUtil.isValidNumber(parseFloat(this.customAmounts[5])) &&
          this.customAmounts[5] === this.donationAmout.toString()
        ) {
          const customAmount = parseFloat(this.customAmounts[5]);
          if (
            customAmount < parseFloat(this.minimumDonationAmount) ||
            customAmount > parseFloat(this.maximumDonationAmount)
          ) {
            newErrors.push(
              new InputErrorModel<DonationPricingPageInputsModel>({
                field: "customAmounts5" as keyof DonationPricingPageInputsModel,
                message: `Kindly enter an amount in between $${this.minimumDonationAmount} - $${this.maximumDonationAmount}`,
              }),
            );
          }
        }
      }
    }
    if (isAutoRun) {
      this.purchaseErrors = getNewErrors(newErrors, this.purchaseErrors);
    } else {
      this.purchaseErrors = newErrors;
    }
    return this.purchaseErrors.length === 0;
  }

  createAPIModel(): IAPIDonationPriceModel {
    const isAllCustom =
      this.pricingOption === ("CustomAll" as keyof DonationPricingOption);
    return {
      title: this.title,
      subTitle: this.subTitle,
      subTitleEditorRawContent: this.subTitleEditorState
        ? JSON.stringify(
            convertToRaw(this.subTitleEditorState.getCurrentContent()),
          )
        : "",
      pricingOption: this.pricingOption,
      type: this.type,
      titleEditorRawContent: this.titleEditorState
        ? JSON.stringify(
            convertToRaw(this.titleEditorState.getCurrentContent()),
          )
        : "",
      description: this.description,
      descriptionEditorRawContent: this.descriptionEditorState
        ? JSON.stringify(
            convertToRaw(this.descriptionEditorState.getCurrentContent()),
          )
        : "",
      messageEditorRawContent: this.messageEditorState
        ? JSON.stringify(
            convertToRaw(this.messageEditorState.getCurrentContent()),
          )
        : "",
      message: this.message,
      // Both branches must send a number: DefaultPrice is a decimal? on the API
      // request and the serializer there rejects a numeric string outright, so
      // falling back to the raw customAmount used to fail the whole save.
      defaultPrice: !this.defaultPrice
        ? this.customAmount
          ? parseFloat(this.customAmount)
          : null
        : parseFloat(this.defaultPrice),
      canSelectOneBeneficiary: this.canSelectOneBeneficiary,
      canSelectMultipleBeneficiary: this.canSelectMultipleBeneficiaries,
      allowComments: this.allowComments,
      inMemoryOf: this.offerInMemoryOf,
      allowAnnualIncrease: this.annualIncrease,
      annualIncreaseDescription: this.annualIncreaseDescription,
      coverFee: this.coverProcessingCost,
      coverFeeMessage: this.coverProcessingCostDescription,
      allowAnonymousDonation: this.allowAnonymousDonation,
      restrictions: this.setLimitOnCustomDonation,
      restrictionSettings: this.setLimitOnCustomDonation
        ? ({
            minimum: this.minimumDonationAmount,
            maximum: this.maximumDonationAmount,
          } as IAPIDonationRestrictionModel)
        : null,
      beneficiaries: this.beneficiaries.map((b) => b.createAPIModel()),
      customAmount1:
        this.pricingOption === ("CustomAll" as keyof DonationPricingOption)
          ? parseFloat(this.customAmounts[0] || "0")
          : this.customAmount
            ? parseFloat(this.customAmount)
            : null,
      customAmount2:
        this.customAmounts?.[1] && isAllCustom
          ? parseFloat(this.customAmounts[1])
          : null,
      customAmount3:
        this.customAmounts?.[2] && isAllCustom
          ? parseFloat(this.customAmounts[2])
          : null,
      customAmount4:
        this.customAmounts?.[3] && isAllCustom
          ? parseFloat(this.customAmounts[3])
          : null,
      customAmount5:
        this.customAmounts?.[4] && isAllCustom
          ? parseFloat(this.customAmounts[4])
          : null,
      customAmount6:
        this.customAmounts?.[5] && isAllCustom
          ? parseFloat(this.customAmounts[5])
          : null,
    } as IAPIDonationPriceModel;
  }

  static deserializeAPIModel(
    data: IAPIDonationPriceModel,
  ): DonationPricingPageInputsModel {
    if (!data) {
      return new DonationPricingPageInputsModel();
    }
    const isAllCustom =
      data.pricingOption === ("CustomAll" as keyof DonationPricingOption);

    const defaultPriceString = data.defaultPrice?.toString() || "";

    const customAmounts = [
      isAllCustom ? data.customAmount1 : "",
      data.customAmount2,
      data.customAmount3,
      data.customAmount4,
      data.customAmount5,
      data.customAmount6,
    ].map((amount) => (amount ? amount.toString() : ""));

    const selectedCustomIndex = customAmounts.findIndex(
      (amount) => amount === defaultPriceString,
    );

    let titleEditorState: EditorState | undefined = undefined;
    if (data.titleEditorRawContent) {
      const v = data.titleEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      titleEditorState = EditorState.createWithContent(es);
    } else {
      titleEditorState = UtilityMethods.getEditorStateFromPlainText(data.title);
    }

    let subTitleEditorState: EditorState | undefined = undefined;
    if (data.subTitleEditorRawContent) {
      const v = data.subTitleEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      subTitleEditorState = EditorState.createWithContent(es);
    } else {
      subTitleEditorState = UtilityMethods.getEditorStateFromPlainText(
        data.subTitle,
      );
    }

    let descriptionEditorState: EditorState | undefined = undefined;
    if (data.descriptionEditorRawContent) {
      const v = data.descriptionEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      descriptionEditorState = EditorState.createWithContent(es);
    } else {
      descriptionEditorState = UtilityMethods.getEditorStateFromPlainText(
        data.description,
      );
    }

    let messageEditorState: EditorState | undefined = undefined;
    if (data.messageEditorRawContent) {
      const v = data.messageEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      messageEditorState = EditorState.createWithContent(es);
    } else {
      messageEditorState = UtilityMethods.getEditorStateFromPlainText(
        data.message,
      );
    }

    const mappedData: Partial<DonationPricingPageInputsModel> = {
      title: data.title,
      subTitle: data.subTitle,
      subTitleEditorState: subTitleEditorState,
      titleEditorState: titleEditorState,
      titleEditorRawContent: data.titleEditorRawContent || data.title,
      subTitleEditorRawContent: data.subTitleEditorRawContent || data.subTitle,
      messageEditorState: messageEditorState,
      messageEditorRawContent: data.messageEditorRawContent || data.message,
      descriptionEditorState: descriptionEditorState,
      descriptionEditorRawContent:
        data.descriptionEditorRawContent || data.description,
      pricingOption: data.pricingOption,
      type: data.type,
      description: data.description,
      message: data.message,
      defaultPrice: defaultPriceString,
      canSelectOneBeneficiary: data.canSelectOneBeneficiary,
      canSelectMultipleBeneficiaries: data.canSelectMultipleBeneficiary,
      allowComments: data.allowComments,
      offerInMemoryOf: data.inMemoryOf,
      annualIncrease: data.allowAnnualIncrease,
      annualIncreaseDescription: data.annualIncreaseDescription,
      coverProcessingCost: data.coverFee,
      coverProcessingCostDescription: data.coverFeeMessage,
      allowAnonymousDonation: data.allowAnonymousDonation,
      setLimitOnCustomDonation: data.restrictions,
      minimumDonationAmount: data.restrictionSettings?.minimum,
      maximumDonationAmount: data.restrictionSettings?.maximum,
      beneficiaries: data.beneficiaries.map((b) => {
        return DonationBeneficiaryModel.deserializeAPIModel(b);
      }),
      donationAmout: defaultPriceString ? parseFloat(defaultPriceString) : 0,
      customAmount:
        data.customAmount1 && !isAllCustom ? data.customAmount1.toString() : "",
      customAmounts: customAmounts,
      selectedCustomIndex: selectedCustomIndex,
    };

    return new DonationPricingPageInputsModel(mappedData);
  }

  static deserializeAPIModelForCustomerFacing(
    data: IAPIDonationPriceModel,
  ): DonationPricingPageInputsModel {
    if (!data) {
      return new DonationPricingPageInputsModel();
    }
    const isAllCustom =
      data.pricingOption === ("CustomAll" as keyof DonationPricingOption);

    const defaultPriceString = data.defaultPrice?.toString() || "";

    const customAmounts = [
      data.customAmount1,
      data.customAmount2,
      data.customAmount3,
      data.customAmount4,
      data.customAmount5,
      data.customAmount6,
    ].map((amount) => (amount ? amount.toString() : ""));
    const selectedCustomIndex = customAmounts.findIndex(
      (amount) => amount === defaultPriceString,
    );

    let titleEditorState: EditorState | undefined = undefined;
    if (data.titleEditorRawContent) {
      const v = data.titleEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      titleEditorState = EditorState.createWithContent(es);
    } else {
      titleEditorState = UtilityMethods.getEditorStateFromPlainText(data.title);
    }

    let subTitleEditorState: EditorState | undefined = undefined;
    if (data.subTitleEditorRawContent) {
      const v = data.subTitleEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      subTitleEditorState = EditorState.createWithContent(es);
    } else {
      subTitleEditorState = UtilityMethods.getEditorStateFromPlainText(
        data.subTitle,
      );
    }

    let descriptionEditorState: EditorState | undefined = undefined;
    if (data.descriptionEditorRawContent) {
      const v = data.descriptionEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      descriptionEditorState = EditorState.createWithContent(es);
    } else {
      descriptionEditorState = UtilityMethods.getEditorStateFromPlainText(
        data.description,
      );
    }

    let messageEditorState: EditorState | undefined = undefined;
    if (data.messageEditorRawContent) {
      const v = data.messageEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      messageEditorState = EditorState.createWithContent(es);
    } else {
      messageEditorState = UtilityMethods.getEditorStateFromPlainText(
        data.message,
      );
    }

    const mappedData: Partial<DonationPricingPageInputsModel> = {
      title: data.title,
      titleEditorRawContent: data.titleEditorRawContent || data.title,
      titleEditorState: titleEditorState,
      subTitle: data.subTitle,
      subTitleEditorRawContent: data.subTitleEditorRawContent || data.subTitle,
      subTitleEditorState: subTitleEditorState,
      messageEditorRawContent: data.messageEditorRawContent || data.message,
      messageEditorState: messageEditorState,
      descriptionEditorRawContent:
        data.descriptionEditorRawContent || data.description,
      descriptionEditorState: descriptionEditorState,
      pricingOption: data.pricingOption,
      type: data.type,
      description: data.description,
      message: data.message,
      defaultPrice: defaultPriceString,
      canSelectOneBeneficiary: data.canSelectOneBeneficiary,
      canSelectMultipleBeneficiaries: data.canSelectMultipleBeneficiary,
      allowComments: data.allowComments ? true : null,
      offerInMemoryOf: data.inMemoryOf ? false : null,
      annualIncrease: data.allowAnnualIncrease ? false : null,
      annualIncreaseDescription: data.annualIncreaseDescription,
      coverProcessingCost: data.coverFee ? false : null,
      coverProcessingCostDescription: data.coverFeeMessage,
      allowAnonymousDonation: data.allowAnonymousDonation ? false : null,
      setLimitOnCustomDonation: data.restrictions,
      minimumDonationAmount: data.restrictionSettings?.minimum,
      maximumDonationAmount: data.restrictionSettings?.maximum,
      beneficiaries: data.beneficiaries.map((b) => {
        return DonationBeneficiaryModel.deserializeAPIModel(b);
      }),
      donationAmout: defaultPriceString ? parseFloat(defaultPriceString) : 0,
      customAmount:
        data.customAmount1 && !isAllCustom ? data.customAmount1.toString() : "",
      customAmounts: customAmounts,
      selectedCustomIndex: selectedCustomIndex,
    };

    return new DonationPricingPageInputsModel(mappedData);
  }

  static deserializeAPIModelForPayment(
    data: IAPIDonationModelForPayment,
  ): DonationPricingPageInputsModel {
    const mappedData: Partial<DonationPricingPageInputsModel> = {
      offerInMemoryOf: data.inMemoryOf,
      inMemoryOfName: data.dedicatedTo,
      inMemoryOfComment: data.tributeMessage,
      selectedBeneficiaries: data.beneficiaries.map((b) => {
        return DonationBeneficiaryModel.deserializeAPIModel(b);
      }),
      oneTime: !data.payMonthly,
    };

    return new DonationPricingPageInputsModel(mappedData);
  }
}

export class DonationBeneficiaryModel {
  id: string;
  name: string;
  constructor(data?: Partial<DonationBeneficiaryModel>) {
    this.id = (data && data.id) || "";
    this.name = (data && data.name) || "";
  }

  createAPIModel(): IAPIDonationBeneficiaryModel {
    return {
      id: this.id,
      name: this.name,
    } as IAPIDonationBeneficiaryModel;
  }

  static deserializeAPIModel(
    data: IAPIDonationBeneficiaryModel,
  ): DonationBeneficiaryModel {
    const mappedData: Partial<DonationBeneficiaryModel> = {
      id: data.id,
      name: data.name,
    };

    return new DonationBeneficiaryModel(mappedData);
  }
}
